import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import dayjs from 'dayjs';
import CDNImageBase64 from './CDNImageBase64';

const e = React.createElement;

type PrintProps = {
	partNumber?: string;
	partImageUrls?: string;
	partUrl?: string;
};

const Print: React.FC<PrintProps> = ({ partNumber, partImageUrls, partUrl }) => {
	const [ dataProjectName, setDataProjectName ] = useState<string>('');
	const [ dataReportName, setDataReportName ] = useState<string>('');
	const [ dataComments, setDataComments ] = useState<string>('');
	const [ dataReportBy, setDataReportBy ] = useState<string>('');
	const [ dataDate, setDataDate ] = useState<string>('');
	const [ dataPartUrl, setDataPartUrl ] = useState<string>(partUrl);

	const [ eLogo, setELogo ] = useState<Element | null>(null);
	const [ eHeader, setEHeader ] = useState<Element | null>(null);
	const [ eIcon, setEIcon ] = useState<Element | null>(null);
	const [ ePartNumber, setEPartNumber ] = useState<Element | null>(null);
	const [ eType, setEType ] = useState<Element | null>(null);
	const [ eDescription, setEDescription ] = useState<Element | null>(null);
	const [ eUpc, setEUpc ] = useState<Element | null>(null);
	const [ eAvailability, setEAvailability ] = useState<Element | null>(null);
	const [ eKeyFeatures, setEKeyFeatures ] = useState<Element | null>(null);
	const [ eGearForceInputs, setEGearForceInputs ] = useState<Element | null>(null);
	const [ eCalculations, setECalculations ] = useState<Element[] | null>(null);
	const [ eCalculationChart, setECalculationChart ] = useState<HTMLCanvasElement | null>(null);
	const [ ePartDetails, setEPartDetails ] = useState<Element | null>(null);
	const [ eNotes, setENotes ] = useState<Element | null>(null);

	useEffect(() => {
		const handleAccountInfoLoaded = async (event) => {
			if (event.detail.accountInfo) {
				const accountInfo = event.detail.accountInfo;

				setDataReportBy([ accountInfo.first_name, accountInfo.last_name ].filter(Boolean).join(' '));
			}
		};

		const handleReportPrint = (event) => {
			const customEvent = event as CustomEvent<any>;
			const data = customEvent.detail;

			runPrintPage(data.projectName, data.reportName, data.comments);
		};

		const handlePartUrl = (event) => {
			if (event?.detail?.partUrl) {
				setDataPartUrl(event.detail.partUrl);
			}
		};

		document.addEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);
		document.addEventListener('reportPrintEvent', handleReportPrint);
		document.addEventListener('partUrlEvent', handlePartUrl);

		return () => {
			document.removeEventListener('timken_sso_account_info_loaded', handleAccountInfoLoaded);
			document.addEventListener('reportPrintEvent', handleReportPrint);
			document.addEventListener('partUrlEvent', handlePartUrl);
		};
	}, []);

	const runPrintPage = (projectName, reportName: '', comments: '') => {
		if (projectName && reportName.length) {
			setDataProjectName(projectName);
			setDataReportName(reportName);
			setDataDate(dayjs().format('MMMM D, YYYY'));
			setDataComments(comments);

			setELogo(document.querySelector('.custom-logo-link'));
			setEHeader(document.querySelector('.t-engineering-tools__header'));
			setEIcon(document.querySelector('.t-engineering-tools__header-icon'));

			setEPartNumber(
				document.querySelector('input[name="partNumber"]') ??
				document.querySelector('.t-parts-details__title')
			);

			setEType(document.querySelector('.m-input__description'));
			setEDescription(document.querySelector('.t-parts-details__description'));
			setEUpc(document.querySelector('.t-parts-details__additional-details__upc'));
			setEAvailability(document.querySelector('.t-parts-details__additional-details__availability'));
			setEKeyFeatures(document.querySelector('.t-parts-details__key-features'));
			setEGearForceInputs(document.querySelector('.t-engineering-tools__gear-force'));
			setECalculations([ ...document.querySelectorAll('.t-engineering-tools__results') ]);
			setECalculationChart(document.querySelector('.graph-and-form-container .graph-section canvas'));
			setEPartDetails(document.querySelector('.t-parts-details__specs'));
			setENotes(document.querySelector('.t-engineering-tools__notes'));
		} else {
			setDataProjectName('');
			setDataReportName('');
			setDataDate('');
			setDataComments('');

			setELogo(null);
			setEHeader(null);
			setEIcon(null);
			setEPartNumber(null);
			setEType(null);
			setEDescription(null);
			setEUpc(null);
			setEAvailability(null);
			setEKeyFeatures(null);
			setEGearForceInputs(null);
			setECalculations(null);
			setECalculationChart(null);
			setEPartDetails(null);
			setENotes(null);
		}
	};

	const getPartUrl = () => {
		let url = '';

		if (dataPartUrl) {
			const origin = window.location.origin;

			url = (! dataPartUrl.startsWith(origin))
				? origin + dataPartUrl
				: dataPartUrl;
		}

		return url;
	};

	const getLogo = () => {
		let src;

		if (window?._timken?.stylesheetURI) {
			src = window._timken.stylesheetURI + '/img/timken-logo-orange.svg';
		} else if (eLogo) {
			const eLogoImage = eLogo.querySelector('img');

			if (eLogoImage && eLogoImage.src) {
				src = eLogoImage.src;
			}
		}

		if (src) {
			return e(
				'img',
				{
					src,
					alt: '',
				}
			);
		}
	};

	const getMainImage = (partUrl: string) => {
		if (partImageUrls) {
			const image = (Array.isArray(partImageUrls) && partImageUrls.length)
				? partImageUrls[ 0 ]
				: partImageUrls;

			return <CDNImageBase64 imageUrl={image} partUrl={partUrl} />;
		}
	};

	const getImages = () => {
		if (partImageUrls && Array.isArray(partImageUrls)) {
			const images: Array<unknown> = [];

			for (let i = 0; i < partImageUrls.length; i++) {
				if (i === 0) {
					continue;
				} else if (i === 4) {
					break;
				}

				images.push(
					<li key={i}>
						<CDNImageBase64 imageUrl={partImageUrls[ i ]} />
					</li>
				);
			}

			if (images.length) {
				return (
					<ul>
						{images}
					</ul>
				);
			}
		}
	};

	const getTitle = () => {
		let title = 'Part Details';

		if (eHeader) {
			title = eHeader.innerText || eHeader.textContent;
		}

		return title;
	};

	const getPartNumber = () => {
		let part;

		if (partNumber) {
			part = partNumber;
		} else if (ePartNumber) {
			if (ePartNumber instanceof HTMLInputElement) {
				part = ePartNumber.value;
			} else {
				part = ePartNumber.innerText || ePartNumber.textContent;
			}
		}

		return part;
	};

	const getType = () => {
		let type;

		if (eType) {
			type = eType.textContent;

			if (type) {
				type = type.replace(/^(Bearing Type\: )/, '');
				type = type.split(' - ').pop().trim();
			}
		}

		return type;
	};

	const getGearForceCalculationsTable = () => {
		if (eGearForceInputs) {
			const classList: string[] = [
				'.m-input__input',
				'.m-select__select',
			];

			const nodeList = eGearForceInputs.querySelectorAll(classList) as
				NodeListOf<HTMLInputElement | HTMLSelectElement>;

			interface MyInputs {
				label: unknown;
				value: unknown;
				unit: unknown;
			}

			const inputs : MyInputs[] = [];

			nodeList.forEach((node) => {
				classList.forEach((ClassMatch) => {
					const classMatch = ClassMatch.slice(1);

					if (node.classList.contains(classMatch)) {
						const regex = /^(.*?)(?=__)/;

						const match = classMatch.match(regex);

						if (match) {
							const name = match[ 1 ];
							const parent = node.parentNode;
							let label,
								value,
								unit;

							if (parent) {
								const nodeLabel = parent.querySelector(`.${ name }__label`);

								if (nodeLabel) {
									for (const child of nodeLabel.childNodes) {
										if (child.nodeType === Node.TEXT_NODE) {
											label = child.nodeValue;
										}
									}
								}

								if (node instanceof HTMLInputElement) {
									value = node.value;

									const nodeUnit = parent.querySelector(`.${ name }__unit`);

									if (nodeUnit) {
										unit = nodeUnit.textContent;
									}
								} else if (node instanceof HTMLSelectElement) {
									const selectedIndex = node.selectedIndex;

									if (selectedIndex !== -1) {
										value = node.options[ selectedIndex ].text;
									}
								}
							}

							if (label) {
								inputs.push({
									label,
									value,
									unit,
								});
							}
						}
					}
				});
			});

			if (inputs?.length) {
				return e(
					'table',
					null,
					e(
						'thead',
						null,
						e(
							'tr',
							null,
							e(
								'th',
								null,
								'Dimensions'
							),
							e(
								'th',
								{ class: 'center-align' },
								'Value'
							),
							e(
								'th',
								{ class: 'center-align' },
								'Units'
							)
						)
					),
					e(
						'tbody',
						null,
						inputs.map(({ label, value, unit }, index) => {
							return e(
								'tr',
								{
									key: index,
								},
								e(
									'td',
									null,
									label
								),
								e(
									'td',
									{ class: 'center-align' },
									value
								),
								e(
									'td',
									{ class: 'center-align' },
									unit
								)
							);
						})
					)
				);
			}
		}
	};

	const getCalculationChartImage = () => {
		if (eCalculationChart) {
			return e(
				'img',
				{
					src: eCalculationChart.toDataURL(),
					alt: '',
				}
			);
		}
	};

	const setInnerHTML = (html, type = '') => {
		return {
			__html:
				typeof html === 'string'
					? html
					: html[ type ],
		};
	};

	return (
		<div id="t-print-report" className='d-none'>
			<div className='container t-print-report'>
				<div className="t-print-report__header">
					<div className="row">
						<div className="col-12">
							<div className="row pb-4 mb-1">
								<div className="col-12">
									{getLogo()}
								</div>
							</div>

							<div className="row pb-3">
								<div className="col-8">
									<div className='d-flex h-100'>
										<div className="row flex-nowrap align-items-center">
											{eIcon &&
												<div className="col-auto pe-0">
													<div className="t-print-report__icon" dangerouslySetInnerHTML={setInnerHTML(eIcon, 'outerHTML')} />
												</div>
											}

											<div className="col">
												<div className="row">
													<div className="col-12">
														<div className="t-print-report__title">
															{getTitle()} Report
														</div>
													</div>

													{(ePartNumber || eType) &&
														<div className="col-12">
															{ePartNumber &&
																<div className="t-print-report__part-number">
																	<span className='fw-bold'>Part Number: </span>
																	<span dangerouslySetInnerHTML={setInnerHTML(getPartNumber())} />
																</div>
															}

															{eType &&
																<div className="t-print-report__bearing-type">
																	<span className='fw-bold'>Bearing Type: </span>
																	<span dangerouslySetInnerHTML={setInnerHTML(getType())} />
																</div>
															}
														</div>
													}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="col-4">
									<div className='d-flex h-100 align-items-center float-end'>
										<div className="row align-items-center">
											<div className="col-auto px-2">
												<span className="t-print-report__product-details fw-lighter">
													Product<br />
													Details:
												</span>
											</div>

											<div className="col-auto px-2">
												{dataPartUrl &&
													<span className="t-print-report__qr-code">
														<QRCode
															size={90}
															viewBox={'0 0 90 90'}
															level={'M'}
															value={getPartUrl()}
														/>
													</span>
												}
											</div>

											<div className="col-auto ps-2">
												{(partImageUrls && partImageUrls.length > 0) &&
												<div className="t-print-report__main-image">
													{ getMainImage(getPartUrl()) }
												</div>
												}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="t-print-report__content py-3">
					<div className="row pb-3">
						<div className="col-6">
							{dataProjectName &&
								<div className="t-print-report__project-name py-1">
									<span className='fw-bold'>Project Name: </span>
									<span dangerouslySetInnerHTML={setInnerHTML(dataProjectName)} />
								</div>
							}

							{dataReportBy &&
								<div className="t-print-report__report-by py-1">
									<span className='fw-bold'>Report By: </span>
									<span dangerouslySetInnerHTML={setInnerHTML(dataReportBy)} />
								</div>
							}

							{dataReportName &&
								<div className="t-print-report__report-name py-1">
									<span className='fw-bold'>Report Name: </span>
									<span dangerouslySetInnerHTML={setInnerHTML(dataReportName)} />
								</div>
							}

							{dataDate &&
								<div className="t-print-report__date py-1">
									<span className='fw-bold'>Date: </span>
									<span dangerouslySetInnerHTML={setInnerHTML(dataDate)} />
								</div>
							}
						</div>

						<div className="col-6">
							<div className="t-print-report__images float-end">
								{getImages()}
							</div>
						</div>
					</div>

					{dataComments &&
						<div className="row pb-4">
							<div className="col-12">
								<div className="t-print-report__comments">
									<span className="fw-bold">Comments: </span>
									<span dangerouslySetInnerHTML={setInnerHTML(dataComments)} />
								</div>
							</div>
						</div>
					}

					{eDescription &&
						<div className="row pb-5">
							<div className="col-6">
								<div className="t-print-report__description" dangerouslySetInnerHTML={setInnerHTML(eDescription, 'innerHTML')} />
							</div>
						</div>
					}

					{(eUpc || eAvailability) &&
						<div className="row pb-4">
							<div className="col-12">
								<div className="row align-items-end">
									<div className="col-auto">
										{eUpc &&
											<div className="t-print-report__upc" dangerouslySetInnerHTML={setInnerHTML(eUpc, 'innerHTML')} />
										}
									</div>

									<div className="col-auto">
										{eAvailability &&
											<div className="t-print-report__availability" dangerouslySetInnerHTML={setInnerHTML(eAvailability, 'innerHTML')} />
										}
									</div>
								</div>
							</div>
						</div>
					}

					{eKeyFeatures &&
						<div className="row pb-4">
							<div className="col-12">
								<div className="t-print-report__key-features" dangerouslySetInnerHTML={setInnerHTML(eKeyFeatures, 'innerHTML')} />
							</div>
						</div>
					}

					{eCalculations &&
						<div className="row pb-4">
							<div className="col-12">
								<div className='t-print-report__calculations'>
									{(! eUpc || ! eAvailability) &&
										<div className="row">
											<div className="col-12">
												<div className='t-print-report__calculations-title'>
													{getTitle()}
												</div>
											</div>
										</div>
									}

									{eGearForceInputs &&
									<div className="row">
										<div className="col-12">
											<div className='c-bearings-table'>
												{getGearForceCalculationsTable()}
											</div>
										</div>
									</div>
									}

									<div className="row">
										<div className="col-12">
											{eCalculations.map((element, index) => (
												<div key={index} dangerouslySetInnerHTML={setInnerHTML(element, 'innerHTML')} />
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					}

					{eCalculationChart &&
						<div className="row pb-4">
							<div className="col-12">
								{getCalculationChartImage()}
							</div>
						</div>
					}

					{ePartDetails &&
						<div className="row pb-4">
							<div className="col-12">
								<div className="t-print-report__part-details" dangerouslySetInnerHTML={setInnerHTML(ePartDetails, 'innerHTML')} />
							</div>
						</div>
					}
				</div>

				{eNotes &&
					<div className="t-print-report__footer">
						<div className="row pb-4">
							<div className="col-12">
								<div className='t-print-report__footer--top'>
									For further information or to connect with a Timken technical expert, visit <span className='text-decoration-underline'>www.engineering.timken.com</span>
								</div>
							</div>
						</div>

						<div className="row pb-4">
							<div className="col-12">
								<div className="t-print-report__footer--bottom" dangerouslySetInnerHTML={setInnerHTML(eNotes, 'innerHTML')} />
							</div>
						</div>
					</div>
				}
			</div>
		</div>
	);
};

export default Print;
